// The poster page's layout (Seán's specs "Adaptive Layout Algorithm" and "Correct Adaptive
// Layout", 2026-10-26): of three arrangements, each with a text column of 40 to 70
// characters, the one that shows the whole presentation (headline, abstract, references and
// the poster) in the window, if one can, and of those the poster largest (PosterPage.vue):
// - stacked: the headline across, the poster below it, then the abstract
// - side: the headline across, then the abstract left of the poster
// - full: the headline and abstract in a column left of the poster, which starts at the top
// - columns: as stacked, the abstract in two balanced columns (spec "Two-Column Abstract")
// Heights are the text's as rendered (measured at each width); the page as wide as its
// arrangement needs, up to the window's. The links are a footer at the bottom of the page
// (spec "Viewport-Filling Layout"): a row (left, centre, right) where it fits, under the whole
// page (beside: below poster and text both, if the window has the room), else one under the
// other (beside: in the text column)

export const GUTTER = 24; // px between the text column and the poster's
export const INSET = 8; // px: the surface left, right and below the poster
export const TEXT_PAD = 12; // px: the text's padding at its left (and right, stacked)
export const COLUMN_GAP = 32; // px between the abstract's two columns
export const TEXT_BLOCK = 24; // px: the abstract's padding above and below
const FOOTER_BLOCK = 24; // px: the footer's space above and its padding below
const HEAD_BLOCK = 12; // px: the headline's below
export const MIN_IMAGE_HEIGHT = 240; // px: the least the poster is given below the header
// average line lengths to try, for 40 to 70 as read (Seán, 2026-10-26: a short abstract in a
// narrow column leaves the poster more room; a long one stays wide enough to fit). Lines break
// at words: a column of 44 reads about 40 a line, and the longest lines of 67 reach 70
const CHARACTERS = [44, 50, 55, 61, 67];
const MIN_IMAGE_WIDTH = 280; // px: the least image column beside the text
const CLOSED_MIN_WIDTH = 640; // px: the closed page's least width (its one-line toolbar)
// none fitting: those within this much of the least scrolling are ranked by their score
const SCROLL_TIE = 48; // px
// stacked's area (and columns') counts this much: beside, the text is in view with the poster
// and the window's width is used, where stacked spends height
const STACKED_SHARE = 1;
// the score: the poster's area × the composition's efficiency to this power (a slightly larger
// poster isn't worth a page much of it empty)
const EFFICIENCY_POWER = 3;
const BELOW = ["stacked", "columns"]; // the text below the poster
const TIE = 0.02; // scores this close are equal: poster and text widths matched (stacked),
// then the less scrolling, then the wider text (a narrower one only where it gains the poster)
const COLUMNS_SAVING = 0.1; // of the window's height: two columns' least saving over one
const HOLD = 0.05; // the layout now is kept while this close to the best (no flicker)

const clamp = (value, least, most) => Math.min(most, Math.max(least, value));
const getArea = ({image}) => image.width * image.height;
export const getKey = ({layout, textColumn}) => `${layout} ${Math.round(textColumn)}`;

// the poster fitted in a box: never stretched; at least `least` tall (MIN_IMAGE_HEIGHT: the
// page then scrolls to it) unless the box's width makes it shorter
function fitImage(maxWidth, maxHeight, aspect, least = MIN_IMAGE_HEIGHT) {
  const height = Math.max(0, Math.min(Math.max(maxHeight, least), maxWidth / aspect));
  return {width: Math.floor(height * aspect), height: Math.floor(height)};
}
const getHead = (input, width) => input.measureHead(width) + HEAD_BLOCK;
const getBody = (input, width, columns = 1) => input.measureBody(width, columns) + TEXT_BLOCK;
// the links: a row (left, centre, right) where it fits the width, else one under the other
function getFooterRowWidth({left, centre, right, gap}) {
  return centre ? centre + 2 * Math.max(left, right) + 2 * gap : left + right + gap;
}
function getFooter(input, width) {
  const isRow = width >= getFooterRowWidth(input.footer);
  const height = isRow ? input.footer.rowHeight : input.footer.columnHeight;
  return {isRow, height: height + FOOTER_BLOCK};
}
// how much of the composition (headline, poster and text, before the page is stretched to the
// window and its footer) the poster and text cover: blocks (width × height) over its box
function getEfficiency(candidate, blocks, height) {
  const used = blocks.reduce((sum, [width, depth]) => sum + width * depth, getArea(candidate));
  return Math.min(1, used / (candidate.contentWidth * height));
}

// below the poster (spec "Consistent Widths in Stacked Layouts"): the abstract in one column
// or two (each `text` wide), and the poster no wider than that text, so they're one block;
// the page that block and its padding. Fitting all: the poster shrinks to leave the abstract
// and links in view, down to MIN_IMAGE_HEIGHT (the text is never shrunk: past that, as much of
// it as can be); else it's as tall as the window allows below the headline
function getBelow(input, {isFittingAll, columns, text}) {
  const textWidth = columns * text + (columns - 1) * COLUMN_GAP;
  const [head, body] = [getHead(input, textWidth), getBody(input, textWidth, columns)];
  const footer = getFooter(input, textWidth);
  const room = input.height - head - INSET - (isFittingAll ? body + footer.height : 0);
  const image = fitImage(textWidth, room, input.aspect);
  const composition = head + image.height + INSET + body;
  const layout = columns > 1 ? "columns" : "stacked";
  const candidate = {layout, image, contentWidth: textWidth + 2 * TEXT_PAD, textColumn: text};
  const efficiency = getEfficiency(candidate, [[textWidth, head + body]], composition);
  return {...candidate, total: composition + footer.height, isFooterRow: footer.isRow, efficiency};
}
// one column: each line length (no wider than the window), and one matching the poster where
// its height leaves it narrower than the text (a portrait), within the line lengths
function getStacked(input) {
  const most = input.width - 2 * TEXT_PAD;
  const texts = [...new Set(CHARACTERS.map((count) => Math.min(input.toPx(count), most)))];
  const [least, widest] = [texts[0], texts.at(-1)];
  return [true, false].flatMap((isFittingAll) => {
    const options = {isFittingAll, columns: 1};
    const each = texts.map((text) => getBelow(input, {...options, text}));
    const matched = clamp(each.at(-1).image.width, least, widest);
    return [...each, getBelow(input, {...options, text: matched})];
  });
}
// two columns where both, their gap and the padding fit the window, and where they save at
// least COLUMNS_SAVING of the window's height over one column of the same line length (a
// few lines in two columns isn't worth it), by their rendered heights; with each line
// length, and columns matching the poster where its height leaves it narrower
function getColumns(input, texts) {
  const isRoomy = (text) => 2 * text + COLUMN_GAP + 2 * TEXT_PAD <= input.width;
  const getSaving = (text) => {
    return input.measureBody(text, 1) - input.measureBody(2 * text + COLUMN_GAP, 2);
  };
  const isWorth = (text) => getSaving(text) >= input.height * COLUMNS_SAVING;
  const usable = texts.filter((text) => isRoomy(text) && isWorth(text));
  if (!usable.length) return [];
  return [true, false].flatMap((isFittingAll) => {
    const options = {isFittingAll, columns: 2};
    const each = usable.map((text) => getBelow(input, {...options, text}));
    const half = (each.at(-1).image.width - COLUMN_GAP) / 2;
    const matched = Math.floor(clamp(half, usable[0], usable.at(-1)));
    return [...each, getBelow(input, {...options, text: matched})];
  });
}

// beside the poster: the image column whatever the text column leaves; the page then only as
// wide as both (the rest of the window left out, not given to the text). Its top level with
// the text's first capitals (imageTop: the title's, full; the abstract's, side)
function getBeside(layout, input, text, headHeight) {
  const room = input.width - TEXT_PAD - text - GUTTER - 2 * INSET;
  const imageTop = input.imageTop[layout];
  const image = fitImage(room, input.height - headHeight - imageTop - INSET, input.aspect);
  const contentWidth = TEXT_PAD + text + GUTTER + image.width + 2 * INSET;
  const imageBottom = headHeight + imageTop + image.height + INSET;
  return {layout, image, contentWidth, textColumn: text, imageTop, imageBottom};
}
// side: the headline across the page, measured at the window's width then at the page's
// the totals with the footer: a row across the whole page, under both poster and text, where
// it fits the page's width and the window's height below them; else one link under the other
// under the text. The composition's height without it (its efficiency: getEfficiency)
function getBesideTotals(candidate, input, blocks, textBottom) {
  const composition = Math.max(candidate.imageBottom, textBottom);
  const efficiency = getEfficiency(candidate, blocks, composition);
  const row = input.footer.rowHeight + FOOTER_BLOCK;
  const isWide = getFooterRowWidth(input.footer) <= candidate.contentWidth - TEXT_PAD - INSET;
  if (isWide && composition + row <= input.height) {
    return {...candidate, total: composition + row, efficiency, isFooterRow: true};
  }
  const footer = input.footer.columnHeight + FOOTER_BLOCK;
  const total = Math.max(candidate.imageBottom, textBottom + footer);
  return {...candidate, total, efficiency, isFooterRow: false};
}
function getSide(input, text) {
  const first = getBeside("side", input, text, getHead(input, input.width - 2 * TEXT_PAD));
  const head = getHead(input, first.contentWidth - 2 * TEXT_PAD);
  const side = getBeside("side", input, text, head);
  const body = getBody(input, text);
  // the headline's band counted at most a line's width (a short title across a wide page
  // leaves most of its band empty)
  const headWidth = Math.min(side.contentWidth - 2 * TEXT_PAD, input.toPx(CHARACTERS.at(-1)));
  const blocks = [[headWidth, head], [text, body]];
  return getBesideTotals(side, input, blocks, head + body);
}
function getFull(input, text) {
  const full = getBeside("full", input, text, 0);
  const column = getHead(input, text) + getBody(input, text);
  return getBesideTotals(full, input, [[text, column]], column);
}

// beside the poster, a narrower column only where it gains the poster (else the wider: fewer
// lines, the same poster): widest first, each kept if its poster is larger than all before
function getGaining(candidates) {
  let largest = 0;
  return candidates.filter((each) => {
    const isGaining = getArea(each) > largest * (1 + TIE);
    largest = Math.max(largest, getArea(each));
    return isGaining;
  });
}

function getCandidates(input) {
  const stacked = getStacked(input);
  const room = input.width - TEXT_PAD - input.toPx(CHARACTERS[0]) - GUTTER - 2 * INSET;
  if (input.isNarrow || room < MIN_IMAGE_WIDTH) return stacked;
  const texts = CHARACTERS.map(input.toPx);
  const widestFirst = [...texts].reverse();
  const beside = [getSide, getFull].flatMap((get) => {
    return getGaining(widestFirst.map((text) => get(input, text)));
  });
  return [...stacked, ...beside, ...getColumns(input, texts)];
}

// how far a stacked poster is from its text's width (none beside: separate columns)
function getMismatch({layout, image, contentWidth}) {
  return BELOW.includes(layout) ? Math.abs(contentWidth - 2 * TEXT_PAD - image.width) : 0;
}
// the largest poster (stacked's counted at STACKED_SHARE); near ties to the matched widths
// (stacked), then the less scrolling, then the wider text
function getScore(each) {
  const share = BELOW.includes(each.layout) ? STACKED_SHARE : 1;
  return getArea(each) * share * each.efficiency ** EFFICIENCY_POWER;
}
function rank(pool) {
  const best = Math.max(...pool.map(getScore));
  const near = pool.filter((each) => getScore(each) >= best * (1 - TIE));
  const getOrder = (each) => [getMismatch(each), each.total, -each.textColumn];
  near.sort((one, other) => {
    const [first, second] = [getOrder(one), getOrder(other)];
    return first.map((value, index) => value - second[index]).find(Boolean) || 0;
  });
  return {best, chosen: near[0]};
}
// all of it in the window first, always (Seán, 2026-10-26: the poster shrinks for the text,
// never the text); if none can, those leaving the least to scroll. Then the best score; the
// layout now kept while near the best of the same pool
function getPool(candidates, input) {
  candidates.forEach((each) => (each.fits = each.total <= input.height + 1));
  const fitting = candidates.filter((each) => each.fits);
  if (fitting.length) return fitting;
  const least = Math.min(...candidates.map((each) => each.total));
  return candidates.filter((each) => each.total <= least + SCROLL_TIE);
}
function chooseCandidate(candidates, input) {
  const pool = getPool(candidates, input);
  const {best, chosen} = rank(pool);
  const isKept = (each) => getKey(each) === input.current && getScore(each) >= best * (1 - HOLD);
  return pool.find(isKept) || chosen;
}

// width: the window's, less the page's margins; height: the window's below the toolbar;
// toPx(characters): a line of that many characters of the abstract's text, in px;
// measureHead(width), measureBody(width, columns): the headline's, and the abstract's (in one
// or two columns) with its references, height at that width (their padding apart); current:
// getKey of the layout now; imageTop: {full, side}, the text's capitals below the row's top;
// footer: the links' widths and heights ({left, centre, right, gap, rowHeight, columnHeight}).
// Returns the layout, its widths (open, and closed), its image's size, whether it all fits, and
// whether its links are a row
export function getPosterLayout(input) {
  const chosen = chooseCandidate(getCandidates(input), input);
  const closedImage = fitImage(input.width - 2 * INSET, input.height - INSET, input.aspect);
  const closedMin = Math.min(input.width, CLOSED_MIN_WIDTH);
  return {...chosen, closedWidth: clamp(closedImage.width + 2 * INSET, closedMin, input.width)};
}
